import { CloseOutlined, Search } from '@mui/icons-material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Card, CardActionArea, CardContent, CardMedia, CircularProgress, IconButton, InputBase, Tab, Tabs } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';
import { useEventFunctions } from '../../../../hooks/useEventFunctions';
import { SupportedPagebuilderThemes } from '../../../../interfaces/pagebuilder';
import { useSelector } from '../../../../reduxToolkit/typedRedux';
import { componentSections, componentType } from '../componentTypes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox, faMap } from '@fortawesome/free-solid-svg-icons';
import { FunctionOverview } from '../../../account/event/Homepage/Functions';

interface AddModalInterface {
  isAddModal: boolean;
  isLandingPage: boolean;
  isNetworkActive?: boolean;
  isAppointmentActive?: boolean;
  isVideoActive?: boolean;
  isChallengeActive?: boolean;
  closeModals: () => void;
  onCreateComponent: (type: string, theme: SupportedPagebuilderThemes) => Promise<void>;
  supportedThemes?: SupportedPagebuilderThemes[];
}

const AddModal = ({
  isAddModal,
  isNetworkActive,
  isChallengeActive,
  isAppointmentActive,
  isVideoActive,
  closeModals,
  onCreateComponent,
  supportedThemes = ['default'],
}: AddModalInterface) => {
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const event = useSelector(({ common }) => common.event);
  const pagedata = useSelector(({ common }) => common.pagedata);
  const PBSelectedComponentIndex = useSelector(({ common }) => common.PBSelectedComponentIndex);
  const PBAddColumnComponent = useSelector(({ common }) => common.PBAddColumnComponent);

  const { t } = useTranslation('pagebuilder');
  const { isFunctionEnabled } = useEventFunctions();

  function filterSeachComponent(c: componentType) {
    if (!search) return true;
    return (
      t(c.name).toLocaleLowerCase().includes(search.toLocaleLowerCase()) ||
      !!c.synonyms?.find((s) => t(`synonyms.${s}`).toLocaleLowerCase().includes(search.toLocaleLowerCase()))
    );
  }

  const _onCreateComponent = async (type: string, theme: SupportedPagebuilderThemes) => {
    setIsLoading(true);
    await onCreateComponent(type, theme);
  };

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute top-0 left-0 w-full h-full z-10 bg-opacity-50 bg-white grid place-items-center">
          <CircularProgress />
        </div>
      )}
      <div onClick={(e) => e.stopPropagation()} className={`h-[70vh] bg-white overflow-y-auto pb-8 modal relative rounded @md:w-4/6`}>
        <div className="flex border top-0 bg-white z-20">
          <Tabs value={activeTab} onChange={(_, value) => setActiveTab(value)} aria-label="basic tabs example">
            <Tab label={t('pagebuilder:add-new-component')} icon={<FontAwesomeIcon icon={faBox} />} iconPosition="start" />
            <Tab label={'Features'} icon={<FontAwesomeIcon icon={faMap} />} iconPosition="start" />
          </Tabs>
        </div>

        {/*<div className="fixed top-0 absolute z-20 opacity-90 flex flex-row sticky p-4 items-center">
          <div className="flex flex-row space-x-1 text-white">
            <AddCircleIcon />
            <p className="font-bold text-lg">{t('pagebuilder:add-new-component')}</p>
          </div>
          <div className="border rounded mx-2 px-2 text-white" style={{ marginLeft: 'auto' }}>
            <InputBase
              onChange={(e) => setSearch(e.target.value)}
              className="text-white h-12"
              placeholder={t('common:search')}
              inputProps={{ 'aria-label': t('common:search') }}
            />
            <Search htmlColor="white" />
          </div>
          <IconButton onClick={closeModals} size="large">
            <CloseOutlined htmlColor="#ffffff" />
          </IconButton>
      </div>*/}
        <div className="relative">
          {activeTab === 0 && (
            <div className="flex flex-col flex-wrap w-full p-3">
              {componentSections
                .filter((cs) =>
                  cs.components.some((c) => !c.for || (!!pagedata?.event && c.for.includes('event')) || (!!pagedata?.team && c.for.includes('team')))
                )
                .filter((cs) => pagedata?.team || cs.hideOnEventPages !== true)
                .filter((cs) => !cs.module || event?.modules[cs.module].active === true)
                .filter((cs) => supportedThemes.includes(cs.theme))
                .filter((cs) => cs.components.filter((c) => filterSeachComponent(c)).length > 0)
                .filter((cs) => cs.components.some((c) => !c.eventFunction || isFunctionEnabled(c.eventFunction, event)))
                .map((s, index: number) => (
                  <span key={'sec-' + index}>
                    <div className="text-slate-800 m-4 mt-6">
                      <p className="text-xl font-bold">{t(s.title)}</p>
                      <p className="text-base text-slate-600">{t(s.description || '')}</p>
                    </div>
                    <div className="flex flex-row flex-wrap w-full ">
                      {s.components
                        .filter((c: componentType) => {
                          return !(process.env.env !== 'dev' && c.productionReady === false);
                        })
                        .filter((c) => !c.for || (!!pagedata?.event && c.for.includes('event')) || (!!pagedata?.team && c.for.includes('team')))
                        .filter((c) => !PBAddColumnComponent || c.type !== 'column')
                        .filter((c) => filterSeachComponent(c))
                        .filter((c) => !c.eventFunction || isFunctionEnabled(c.eventFunction, event))
                        .map(
                          (c: componentType, index: number) =>
                            (c.type != 'video' || isVideoActive) &&
                            (c.type != 'network' || isNetworkActive) &&
                            (c.type != 'challenge' || isChallengeActive) &&
                            (c.type != 'appointment' || isAppointmentActive) && (
                              <div key={'comp-' + index} className="p-1 componentCardWrapper">
                                <div className="w-[300px] m-2.5 h-full hover:shadow-lg">
                                  <Card style={{ height: '100%' }} onClick={() => _onCreateComponent(c.type, s.theme)}>
                                    <CardActionArea style={{ height: '100%' }}>
                                      {!s.hideIcons && (
                                        <CardMedia>
                                          <div
                                            style={{
                                              minHeight: '140px',
                                              display: 'grid',
                                              placeItems: 'center',
                                            }}>
                                            {c.icon}
                                          </div>
                                        </CardMedia>
                                      )}
                                      <CardContent>
                                        <p className="text-xl font-bold text-slate-700 text-center">{t(c.name)}</p>
                                        <p className="text-base text-slate-400 text-center">{t(c.description)}</p>
                                      </CardContent>
                                    </CardActionArea>
                                  </Card>
                                </div>
                              </div>
                            )
                        )}
                    </div>
                  </span>
                ))}
            </div>
          )}
          {activeTab === 1 && (
            <div className="w-full p-4">
              <FunctionOverview />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
export default AddModal;
